import { retainSimulationScheduling, saveSimulationSchedule } from '../Utils/simulation_scheduling';
import { saveSimulationLighting } from '../Utils/simulation_lighting';
import {
  CreateSimulationInfoGraphs,
  GenerateSimulationHistory,
  SimulationGetSendValueRequest,
  CreateSimulationHistoryBlock,
  SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK,
  SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK
} from '../Utils/simulation_utils.js';

import {
  createSimulationModel,
  saveSimulationSettings,
  syncSimulationZones
} from '../Utils/simulation_presets';
import { resolutions } from '../Constants/constants';
import { websocketDebug } from '../Utils/websocket_logging.js';
import { retainSimulationNutrition, saveSimulationNutrition, simulationTankRefill } from '../Utils/simulation_nutrition';

export default function simulationOnMessage(simulation, resp) {
  if (!simulation) return;
  const username = simulation.username;
  simulation.zones = simulation.zones || [];
  simulation.hvac = simulation.hvac || { updated: false, data: null };
  simulation.model =
    simulation.model || createSimulationModel(simulation.controller.type, simulation.zones);
  //Try to parse command.
  let command = '';
  try {
    command = resp['command'];
  } catch (err) {
    return;
  }
  websocketDebug('[simulation-ws] received', {
    command,
    controllerId: simulation.controller?.id
  });

  const history = (...args) =>
    GenerateSimulationHistory(...args, {
      model: simulation.model,
      zoneId: String(resp.data?.id ?? '0')
    });

  //Setup initial response
  var json_response = { username: username, command: '' };

  // request_values -> send values to server
  if (command === 'request_values') {
    // If a new zone was recently added, send full-values again
    if (simulation.has_new_zone) {
      json_response = SimulationGetSendValueRequest(
        true,
        simulation.controller.id,
        simulation.zones,
        simulation.hvac,
        simulation.model
      );
      simulation.has_new_zone = false;
      simulation.hvac.updated = false;
    } else {
      json_response = SimulationGetSendValueRequest(
        false,
        simulation.controller.id,
        simulation.zones,
        simulation.hvac,
        simulation.model
      );
      simulation.hvac.updated = false;
    }
  } else if (command === 'request_values_full') {
    json_response = SimulationGetSendValueRequest(
      true,
      simulation.controller.id,
      simulation.zones,
      simulation.hvac,
      simulation.model
    );
    simulation.hvac.updated = false;
  }
  // action -> acknowledge supported simulation actions
  else if (command === 'action') {
    const data = resp['data'];
    const manual = /^action_irrigation_manual_shot_([0-3])$/.exec(data.id);
    if (data.id?.startsWith('action_tank_refill_')) {
      json_response.command='message';
      json_response.data=simulationTankRefill(simulation,data);
    } else if (['action_restart_wifi_module', 'action_restart_maincontroller'].includes(data.id)) {
      // Simulation has no firmware process to reboot.
      json_response.command = 'message';
      json_response.data = {
        type: 'info', message_parts: ['simulation_restart_unavailable'],
        timestamp: Date.now(), dont_log: false
      };
    } else if (manual || data.id === 'action_irrigation_reset_adaptive') {
      let key = 'irrigation_action_unavailable';
      let accepted = false;
      const zoneId = simulation.model.type === 5155 ? data.zone_id : 0;
      if ([5055, 5155].includes(simulation.model.type) && simulation.model.rooms[zoneId]) {
        const index = manual ? Number(manual[1]) : data.value;
        if (!Number.isInteger(data.value) || data.value < 0 || data.value > (manual ? 3600 : 3)) {
          key = 'irrigation_action_invalid';
        } else if (!manual) {
          key = 'gc_ms_irr_adaptive_reset_done';
          accepted = true; // UI simulation has no durable adaptive model.
        } else {
          simulation.model.manualIrrigationUntil = simulation.model.manualIrrigationUntil || {};
          const channel = `${zoneId}:${index}`;
          const active = (simulation.model.manualIrrigationUntil[channel] ?? 0) > Date.now();
          if (data.value > 0 && active) key = 'gc_ms_irr_manual_busy';
          else {
            simulation.model.manualIrrigationUntil[channel] = Date.now() + data.value * 1000;
            key = data.value === 0 ? 'gc_ms_irr_manual_stopped' : 'gc_ms_irr_manual_started';
            accepted = true;
          }
        }
      }
      json_response.command = 'message';
      json_response.data = { type: accepted ? 'info' : 'error', message_parts: [key], timestamp: Date.now() };
    } else if (['action_root_zone_read_address', 'action_root_zone_change_address'].includes(data.id)) {
      // Simulation has no physical SDI-12 bus; never manufacture an address result.
      json_response.command = 'message';
      json_response.data = {
        type: 'error', message_parts: ['gc_ms_root_addr_unavailable'],
        timestamp: Date.now(), dont_log: false
      };
    }
    // Only answer if action is not the delete-controller-action.
    else if (data['id'] !== 'reset_controller_credentials') {
      json_response['command'] = 'resp_action';
      json_response['data'] = { success: true };
      json_response['data']['info'] = { id: data['id'] };
      if ('value' in data) json_response['data']['info']['value'] = data['value'];
    }
  }
  // update_setpoints -> send error or success message.
  else if (command === 'update_setpoints') {
    if (resp.data?.light_channels) {
      json_response.command = 'message';
      json_response.data = saveSimulationLighting(simulation, resp.data);
      if (json_response.data.type === 'error') {
        if (simulation.socket?.readyState === 1) simulation.sendWSMsg({
          ...json_response, internal_id: simulation.controller.id, socket: simulation.socket
        });
        json_response.data = { ...json_response.data, dont_log: false,
          message_parts: ['lights', ': ', 'gc_ms_nothing_changed', ' - 1 ',
            'gc_ms_setting_rejected', '1, '] };
      }
    } else if (resp.data?.schedule_meta || resp.data?.schedule_values) {
      json_response.command = 'message';
      json_response.data = saveSimulationSchedule(simulation, resp.data);
      if (json_response.data.type === 'error') {
        // Match ESP's lifecycle event followed by SAM's ordinary save result.
        if (simulation.socket?.readyState === 1) simulation.sendWSMsg({
          ...json_response, internal_id: simulation.controller.id, socket: simulation.socket
        });
        json_response.data = {
          ...json_response.data,
          dont_log: false,
          message_parts: ['scheduling', ': ', 'gc_ms_nothing_changed', ' - 1 ',
            'gc_ms_setting_rejected', '1, ']
        };
      }
    } else if (resp.data?.nutrition) {
      json_response.command = 'message';
      json_response.data = saveSimulationNutrition(simulation, resp.data);
    } else {
      // Construct response message.
      const category =
        resp.data && typeof resp.data === 'object'
          ? Object.keys(resp.data).find(
              (key) => !['zone', 'id', 'name', 'has_zone_data'].includes(key)
            )
          : '';
      const categoryTranslationKey = category === 'hvac' ? 'hvac_cat' : category;
      json_response['command'] = 'message';
      json_response['data'] = {};
      if (saveSimulationSettings(simulation.model, resp.data)) {
        json_response['data']['type'] = 'success';
        json_response['data']['message_parts'] = [
          categoryTranslationKey,
          ': ',
          'all',
          ' ',
          'gc_ms_settings_accepted'
        ];
        simulation.has_new_zone = true; // Return the saved configuration on the next poll.
      } else {
        json_response['data']['type'] = 'error';
        json_response['data']['message_parts'] = [
          categoryTranslationKey,
          ': ',
          'gc_ms_nothing_changed',
          ' - 1 ',
          'gc_ms_setting_rejected'
        ];
      }
      json_response.data.timestamp = Date.now();
    }
  } else if (command === 'clear_msg_log') {
    json_response['command'] = 'send_all_messages';
    json_response['internal_id'] = simulation.id;
    json_response['data'] = { all_messages: [] };
  } else if (command === 'get_all_messages') {
    // json_response["command"] = "send_all_messages";
    // var date = new Date();
    // json_response["data"] = {
    //   "all_messages":[
    //     {
    //       "message_parts":["test_message", " ", "test_message_2"],
    //       "type":"info",
    //       "timestamp":date.getTime(),
    //     }
    //   ]
    // };
  } else if (command === 'get_recent_history') {
    const date = new Date();
    json_response['command'] = 'send_recent_history';
    json_response['internal_id'] = simulation.controller.id;
    json_response['is_zoned'] = simulation.zones.length > 0;
    // Send 1. block
    var history_block = history(60, resolutions.recent, 0, 2);
    history_block['progress'] = 0;
    history_block['part'] = 0;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 2. block
    history_block = history(60, resolutions.recent, 1, 2);
    history_block['progress'] = 100;
    history_block['part'] = 1;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    json_response['command'] = '';
  } else if (command === 'get_daily_history') {
    console.log('[hist] handling `get_daily_history`');
    const date = new Date();
    json_response['command'] = 'send_daily_history';
    json_response['internal_id'] = simulation.controller.id;
    json_response['is_zoned'] = simulation.zones.length > 0;
    // Send 1. block
    history_block = history(SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK, resolutions.daily, 0, 2);
    history_block['progress'] = 0;
    history_block['part'] = 0;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 2. block
    history_block = history(SIMULATION_48_HOUR_HISTORY_POINTS_PER_BLOCK, resolutions.daily, 1, 2);
    history_block['progress'] = 100;
    history_block['part'] = 1;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    json_response['command'] = '';
    console.log('[hist] handling `get_daily_history` done.');
  } else if (command === 'get_history') {
    const date = new Date();
    json_response['command'] = 'send_history';
    json_response['internal_id'] = simulation.controller.id;
    json_response['is_zoned'] = simulation.zones.length > 0;
    // Send 1. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 0, 6);
    history_block['progress'] = 0;
    history_block['part'] = 0;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 2. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 1, 6);
    history_block['progress'] = 20;
    history_block['timestamp'] = date.getTime();
    history_block['part'] = 1;
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 3. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 2, 6);
    history_block['progress'] = 40;
    history_block['part'] = 2;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 4. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 3, 6);
    history_block['progress'] = 60;
    history_block['part'] = 3;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 5. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 4, 6);
    history_block['progress'] = 80;
    history_block['timestamp'] = date.getTime();
    history_block['part'] = 4;
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    // Send 6. block
    history_block = history(SIMULATION_FULL_HISTORY_POINTS_PER_BLOCK, resolutions.full, 5, 6);
    history_block['progress'] = 100;
    history_block['part'] = 5;
    history_block['timestamp'] = date.getTime();
    json_response['data'] = CreateSimulationHistoryBlock(
      history_block,
      simulation.zones,
      resp.data
    );
    simulation.socket.send(JSON.stringify(json_response));
    json_response['command'] = '';
  } else if (command === 'resolve_alarm') {
    json_response['command'] = 'alarms_resolved';
    const timestamp = new Date().getTime();
    json_response['data'] = {};
    if (resp.data.identity) json_response.data.alarm_instances = [resp.data.identity];
    else {
      json_response.command = 'message';
      json_response.data = {
        message_parts: ['alarm_clear_unavailable'],
        type: 'error',
        dont_log: true
      };
    }
    json_response['data']['timestamp'] = timestamp;
  } else if (command === 'get_info_graphs') {
    json_response['command'] = 'send_info_graphs';
    json_response['internal_id'] = simulation.controller.id;
    // InfoGraphs [zoned]
    if (simulation.zones.length > 0) {
      json_response['is_zoned'] = true;
      json_response['data'] = { zones: {} };
      for (let z of simulation.zones) {
        json_response['data']['zones'][z.id] = CreateSimulationInfoGraphs(simulation.model, z.id);
      }
    }
    // InfoGraphs [Base]
    else {
      json_response['data'] = CreateSimulationInfoGraphs(simulation.model);
    }
  } else if (command === 'get_fs_status') {
    console.log('[sumilation-ws] handling cmd `get_fs_status`');
    json_response['command'] = 'fs_status';
    json_response['data'] = {};
    json_response['data']['total_size'] = 33138688;
    json_response['data']['free'] = 42;
    json_response['data']['flash_status'] = 1;
    json_response['data']['fs_status'] = 1;
  } else if (command === 'update_display_name') {
    json_response.command = 'update_display_name';
    json_response.data = { display_name: resp.data.display_name };
  } else if (command === 'rename_zone') {
    const data = resp['data'];
    for (var i = 0; i < simulation.zones.length; i++) {
      if (String(simulation.zones[i].id) === String(data.id)) simulation.zones[i].name = data.name;
    }
  } else if (command === 'add_zone') {
    const data = resp['data'];
    const occupiedIds = new Set(simulation.zones.map((zone) => String(zone.id)));
    let id = 0;
    while (occupiedIds.has(String(id))) id += 1;
    if (id < 6) simulation.zones.push({ id: String(id), name: data.name });
    syncSimulationZones(simulation.model, simulation.zones);
    simulation.has_new_zone = true;
  } else if (command === 'delete_zone') {
    const data = resp['data'];
    simulation.zones = simulation.zones.filter((zone) => {
      return String(zone.id) !== String(data.id);
    });
    syncSimulationZones(simulation.model, simulation.zones);
    simulation.has_new_zone = true;
  }

  // Send response, when command was set:
  if (json_response.command === 'update_values') {
    retainSimulationNutrition(simulation, json_response);
    retainSimulationScheduling(simulation, json_response);
  }
  if (json_response['command'] !== '') {
    if (simulation.socket !== undefined && simulation.socket.readyState === 1) {
      json_response['internal_id'] = simulation.controller.id;
      json_response.socket = simulation.socket;

      websocketDebug('[simulation-ws] sent', {
        command: json_response.command,
        controllerId: simulation.controller?.id
      });
      simulation.sendWSMsg(json_response);
    }
  }
}
